import {
    cloneElement,
    isValidElement,
    type FC,
    type MouseEvent,
    type ReactElement,
    type Ref,
} from 'react'

import { FloatingFocusManager, FloatingPortal } from '@floating-ui/react'

import { OverflowMenuProvider, useOverflowMenuContext } from './context'
import { useMergeRefs } from './useMergeRefs'
import { ErrorIcon, WarningIcon, CheckIcon } from './icons'
import type {
    OverflowMenuButtonTextProps,
    OverflowMenuCheckedProps,
    OverflowMenuContentProps,
    OverflowMenuErrorMessageProps,
    OverflowMenuIconProps,
    OverflowMenuListItemProps,
    OverflowMenuProps,
    OverflowMenuRightIconProps,
    OverflowMenuSeparatorProps,
    OverflowMenuSubtitleProps,
    OverflowMenuTriggerProps,
    OverflowMenuWarningMessageProps,
} from './types'
import { cx } from './utils'

import './OverflowMenu.scss'

const OverflowMenuRoot: FC<OverflowMenuProps> = ({
    className,
    children,
    open,
    defaultOpen,
    onOpenChange,
    closeOnSelect,
    placement,
    trigger,
    ...rest
}) => {
    return (
        <OverflowMenuProvider
            open={open}
            defaultOpen={defaultOpen}
            onOpenChange={onOpenChange}
            closeOnSelect={closeOnSelect}
            placement={placement}
            trigger={trigger}
        >
            <div className={cx('OverflowMenu', className, open && 'OverflowMenu--open')} {...rest}>
                {children}
            </div>
        </OverflowMenuProvider>
    )
}

OverflowMenuRoot.displayName = 'OverflowMenu'

const Trigger: FC<OverflowMenuTriggerProps> = ({ children, className }) => {
    const { open, refs, getReferenceProps } = useOverflowMenuContext()

    if (!isValidElement(children)) {
        throw new Error('OverflowMenu.Trigger expects a single React element child')
    }

    const child = children as ReactElement<{
        className?: string
        onClick?: (event: MouseEvent) => void
    }> & { ref?: Ref<HTMLElement> }

    const mergedRef = useMergeRefs([refs.setReference, child.ref])
    const referenceProps = getReferenceProps({
        ref: mergedRef,
        'aria-expanded': open,
        'aria-haspopup': 'menu',
        className: cx(child.props.className, className),
        onClick: child.props.onClick,
    })

    return cloneElement(child, referenceProps)
}

Trigger.displayName = 'OverflowMenu.Trigger'

const Content: FC<OverflowMenuContentProps> = ({ className, children, style, ...rest }) => {
    const { open, refs, floatingStyles, getFloatingProps, context } = useOverflowMenuContext()

    if (!open) return null

    return (
        <FloatingPortal>
            <FloatingFocusManager
                context={context}
                modal={false}
                initialFocus={-1}
                returnFocus={false}
            >
                <div
                    className={cx('OverflowMenu__content', className)}
                    ref={refs.setFloating}
                    style={{ ...floatingStyles, zIndex: 10000, ...style }}
                    {...getFloatingProps()}
                    {...rest}
                >
                    {children}
                </div>
            </FloatingFocusManager>
        </FloatingPortal>
    )
}

Content.displayName = 'OverflowMenu.Content'

const ListItem: FC<OverflowMenuListItemProps> = ({
    className,
    children,
    disabled = false,
    type = 'button',
    onClick,
    ...rest
}) => {
    const { setOpen, closeOnSelect } = useOverflowMenuContext()

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        if (disabled) {
            event.preventDefault()
            return
        }
        onClick?.(event)
        if (!event.defaultPrevented && closeOnSelect) {
            setOpen(false)
        }
    }

    return (
        <button
            type={type}
            role="menuitem"
            className={cx('OverflowMenu__listItem', className)}
            disabled={disabled}
            aria-disabled={disabled || undefined}
            tabIndex={disabled ? -1 : 0}
            onClick={handleClick}
            {...rest}
        >
            {children}
        </button>
    )
}

ListItem.displayName = 'OverflowMenu.ListItem'

const Separator: FC<OverflowMenuSeparatorProps> = ({ className, ...rest }) => {
    return <hr role="separator" className={cx('OverflowMenu__separator', className)} {...rest} />
}

Separator.displayName = 'OverflowMenu.Separator'

const Subtitle: FC<OverflowMenuSubtitleProps> = ({ className, children, ...rest }) => {
    return (
        <div className={cx('OverflowMenu__subtitle', className)} {...rest}>
            <span className="OverflowMenu__subtitleText">{children}</span>
            <span className="OverflowMenu__subtitleLine" aria-hidden />
        </div>
    )
}

Subtitle.displayName = 'OverflowMenu.Subtitle'

const ButtonText: FC<OverflowMenuButtonTextProps> = ({ className, children, ...rest }) => {
    return (
        <span className={cx('OverflowMenu__buttonText', className)} {...rest}>
            {children}
        </span>
    )
}

ButtonText.displayName = 'OverflowMenu.ButtonText'

const Icon: FC<OverflowMenuIconProps> = ({ className, children, ...rest }) => {
    return (
        <span className={cx('OverflowMenu__icon', className)} aria-hidden {...rest}>
            {children}
        </span>
    )
}

Icon.displayName = 'OverflowMenu.Icon'

const RightIcon: FC<OverflowMenuRightIconProps> = ({ className, children, ...rest }) => {
    return (
        <span className={cx('OverflowMenu__rightIcon', className)} aria-hidden {...rest}>
            {children}
        </span>
    )
}

RightIcon.displayName = 'OverflowMenu.RightIcon'

const Checked: FC<OverflowMenuCheckedProps> = ({ className, children, ...rest }) => {
    return (
        <span className={cx('OverflowMenu__checked', className)} aria-hidden {...rest}>
            {children ?? <CheckIcon />}
        </span>
    )
}

Checked.displayName = 'OverflowMenu.Checked'

const WarningMessage: FC<OverflowMenuWarningMessageProps> = ({ className, children, ...rest }) => {
    return (
        <span className={cx('OverflowMenu__warningMessage', className)} {...rest}>
            <WarningIcon />
            {children}
        </span>
    )
}

WarningMessage.displayName = 'OverflowMenu.WarningMessage'

const ErrorMessage: FC<OverflowMenuErrorMessageProps> = ({ className, children, ...rest }) => {
    return (
        <span className={cx('OverflowMenu__errorMessage', className)} {...rest}>
            <ErrorIcon />
            {children}
        </span>
    )
}

ErrorMessage.displayName = 'OverflowMenu.ErrorMessage'

export const OverflowMenu = Object.assign(OverflowMenuRoot, {
    Trigger,
    Content,
    ListItem,
    Separator,
    Subtitle,
    ButtonText,
    Icon,
    RightIcon,
    Checked,
    WarningMessage,
    ErrorMessage,
})

export default OverflowMenu
